import React, { useEffect, useRef, useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Modal from "@/components/Modals/Modal";
import AmbassadorsList from "./AmbassadorsList";
import { getAmbassadoresDeEvento } from "@/features/evento/eventoThunks";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";

/**
 * AmbassadorsModal - Modal con scroll infinito para mostrar todos los embajadores del evento
 * @param {Object} props
 * @param {boolean} props.isOpen - Controla si el modal está abierto
 * @param {Function} props.onClose - Función para cerrar el modal
 * @param {string} props.uidEvento - UID del evento
 * @returns {JSX.Element}
 */
export default function AmbassadorsModal({ isOpen, onClose, uidEvento }) {
  const dispatch = useDispatch();
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  
  // Redux state
  const {
    ambassadoresEvento,
    ambassadoresLoading,
    ambassadoresHasNextPage,
    ambassadoresCurrentPage,
  } = useSelector((state) => state.evento);

  console.log("AmbassadorsModal - ", ambassadoresEvento);
  
  // Refs para infinite scroll
  const observerRef = useRef(null);
  const loadingRef = useRef(null);

  // Cargar primera página cuando se abre el modal
  useEffect(() => {
    if (isOpen && uidEvento) {
      setIsInitialLoad(true);
      dispatch(getAmbassadoresDeEvento({ 
        uidEvento, 
        page: 1, 
        limit: 6 
      })).finally(() => {
        setIsInitialLoad(false);
      });
    }
  }, [isOpen, uidEvento, dispatch]);

  // Handler para IntersectionObserver
  const handleObserver = useCallback(
    (entries) => {
      const target = entries[0];
      if (
        target.isIntersecting &&
        ambassadoresHasNextPage &&
        !ambassadoresLoading &&
        !isInitialLoad &&
        isOpen
      ) {
        dispatch(
          getAmbassadoresDeEvento({
            uidEvento,
            page: ambassadoresCurrentPage + 1,
            limit: 6,
          })
        );
      }
    },
    [
      ambassadoresHasNextPage,
      ambassadoresLoading,
      ambassadoresCurrentPage,
      uidEvento,
      dispatch,
      isInitialLoad,
      isOpen,
    ]
  );

  // Setup IntersectionObserver
  useEffect(() => {
    const observer = new IntersectionObserver(handleObserver, {
      root: null,
      rootMargin: "20px",
      threshold: 0.1,
    });

    observerRef.current = observer;
    const currentLoadingRef = loadingRef.current;

    if (currentLoadingRef && isOpen) {
      observer.observe(currentLoadingRef);
    }

    return () => {
      if (currentLoadingRef) {
        observer.unobserve(currentLoadingRef);
      }
    };
  }, [handleObserver, isOpen]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} large="max-w-3xl">
      <div className="p-6 bg-aw-bgbluedark rounded-lg inset-shadow-sm inset-shadow-aw-green shadow-sm shadow-aw-green">
        {/* Header */}
        <h2 className="text-2xl font-bold text-white mb-6 text-center">
          Staff Oficial de Embajadores
        </h2>

        {/* Content */}
        <div className="max-h-[60vh] overflow-y-auto pr-2">
          {isInitialLoad ? (
            // Loading inicial
            <div className="flex items-center justify-center py-12">
              <FontAwesomeIcon
                icon={faSpinner}
                className="text-aw-green text-4xl animate-spin"
              />
            </div>
          ) : ambassadoresEvento && ambassadoresEvento.length > 0 ? (
            <>
              {/* Lista de embajadores */}
              <AmbassadorsList ambassadors={ambassadoresEvento} />

              {/* Loading indicator para infinite scroll */}
              <div
                ref={loadingRef}
                className="h-16 flex items-center justify-center mt-4"
              >
                {ambassadoresLoading && (
                  <FontAwesomeIcon
                    icon={faSpinner}
                    className="text-aw-green text-2xl animate-spin"
                  />
                )}
                {!ambassadoresHasNextPage && ambassadoresEvento.length > 0 && (
                  <p className="text-gray-400 text-sm">
                    No hay más embajadores
                  </p>
                )}
              </div>
            </>
          ) : (
            // Sin embajadores
            <div className="text-center text-gray-400 py-12">
              No hay embajadores disponibles para este evento
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
